/**
 * Ops queue: compensation installments due on or before a date.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
} from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { format } from "date-fns";
import { formatMoney } from "../utils/formatMoney.js";
import { getDueInstallments } from "../services/compensationService.js";
import { DUE_QUEUE } from "../copy/opsDescriptions.js";
import {
  PageShell,
  PageHeader,
  PageFilters,
} from "@/shared/components/pageShell";

export default function DueCompensationView() {
  const navigate = useNavigate();
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const iso = asOf ? new Date(`${asOf}T23:59:59`).toISOString() : undefined;
    const res = await getDueInstallments(iso);
    if (res.success) {
      setRows(res.data || []);
    } else {
      setError(res.error);
      setRows([]);
    }
    setLoading(false);
  }, [asOf]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageShell sx={{ maxWidth: 1200, mx: "auto" }}>
      <PageHeader
        title={DUE_QUEUE.title}
        subtitle={DUE_QUEUE.intro}
        back={{
          label: "Finance",
          shortLabel: "Finance",
          onClick: () => navigate("/finance"),
        }}
      />

      <PageFilters>
        <TextField
          label="Due on or before"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          sx={{ bgcolor: "background.paper" }}
        />
        <Button
          variant="outlined"
          onClick={load}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          Refresh
        </Button>
      </PageFilters>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Installment</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    No installments due for this date.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={`${row.planId}-${row.installment.sequence}`}>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {row.jobId}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {row.planId}
                    </TableCell>
                    <TableCell>#{row.installment.sequence}</TableCell>
                    <TableCell>
                      {row.installment.dueDate
                        ? format(new Date(row.installment.dueDate), "PP")
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(row.installment.amount)}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        endIcon={<OpenInNew />}
                        onClick={() =>
                          navigate(`/orders/${row.jobId}/compensation`)
                        }
                        sx={{ textTransform: "none" }}
                      >
                        Open order
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ open: false, message: "" })}
        message={notification.message}
      />
    </PageShell>
  );
}
